import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { SharedCookieService } from './shared-cookie.service';

export interface KeycloakTokens {
  accessToken: string | undefined;
  refreshToken: string | undefined;
  idToken: string | undefined;
  idTokenParsed: Keycloak.KeycloakTokenParsed | undefined;
  accessTokenParsed: Keycloak.KeycloakTokenParsed | undefined;
  authenticated: boolean;
  expiresAtMs: number;
}

/**
 * Thin wrapper around keycloak-js implementing the OIDC Authorization Code Flow with PKCE.
 * Replaces the legacy ROPC/Direct Access Grants flow to enable Keycloak SSO across Edara 2.0 and 3.0.
 * See ADR-011 for the migration rationale.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private keycloak: Keycloak | null = null;
  private initPromise: Promise<boolean> | null = null;

  /** Upper bound on the bootstrap SSO check — see the note in [[init]]. */
  private static readonly INIT_TIMEOUT_MS = 15000;

  private readonly scope = `email profile ${environment.keycloakConfig.apiIdentifier}`;

  constructor(private sharedCookie: SharedCookieService) { }

  public get instance(): Keycloak | null {
    return this.keycloak;
  }

  public get tokens(): KeycloakTokens | null {
    if (!this.keycloak) return null;
    const expSeconds = this.keycloak.tokenParsed?.exp ?? 0;
    return {
      accessToken: this.keycloak.token,
      refreshToken: this.keycloak.refreshToken,
      idToken: this.keycloak.idToken,
      idTokenParsed: this.keycloak.idTokenParsed,
      accessTokenParsed: this.keycloak.tokenParsed,
      authenticated: !!this.keycloak.authenticated,
      expiresAtMs: expSeconds * 1000
    };
  }

  /**
   * Initialize keycloak-js. Called from APP_INITIALIZER so SSO state is resolved before any
   * route/guard activates. On the OIDC callback URL, this consumes the `code` parameter and
   * exchanges it for tokens. Otherwise it performs a silent SSO check via hidden iframe.
   */
  public init(): Promise<boolean> {
    if (this.initPromise) return this.initPromise;

    this.keycloak = new Keycloak({
      url: environment.keycloakConfig.url,
      realm: environment.keycloakConfig.realm,
      clientId: environment.keycloakConfig.clientId
    });

    // Restore the previous session's tokens from localStorage so keycloak-js holds an in-memory
    // refresh token after a full page load (refresh / new tab). Without this, the silent check-sso
    // iframe is the only thing that re-establishes the in-memory session — and it fails whenever
    // 3rd-party cookies are blocked (Safari, incognito, Chrome's 3PCD), leaving keycloak
    // unauthenticated even though valid tokens still exist in localStorage. keycloak-js validates the
    // seeded tokens by forcing a refresh_token grant (updateToken(-1)) during init; that grant is
    // carried in the request body and does NOT depend on cookies, so it re-establishes the session
    // even when the iframe check can't. If the seeded refresh token is expired/rotated the grant
    // fails and keycloak-js falls back to the (iframe-only, no-redirect) check-sso — so seeding can
    // neither log the user out nor reintroduce bug 26827. keycloak-js only adopts the seeded session
    // when BOTH token and refreshToken are present, so we seed them together.
    const seededTokens: { token?: string; refreshToken?: string; idToken?: string } = {};
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (storedAccessToken && storedRefreshToken) {
      seededTokens.token = storedAccessToken;
      seededTokens.refreshToken = storedRefreshToken;
      const storedIdToken = localStorage.getItem('idToken');
      if (storedIdToken) seededTokens.idToken = storedIdToken;
    }

    // keycloak-js puts NO timeout on the silent check-sso wait: if the hidden iframe never
    // reports back — a proxy/WAF answering the authorization request, a missing
    // silent-check-sso.html redirect URI, storage partitioning — init() never settles. Because
    // it runs in an APP_INITIALIZER, the app then never bootstraps and the user is left on the
    // loading spinner forever, with no way forward.
    //
    // Resolve as "not authenticated" after a bounded wait instead. Standalone, the auth guard
    // then sends the user to /auth/login for a normal first-party redirect. Embedded, it lets
    // the guard run at all — which is the only way the portal ever hears `auth-required` and
    // can offer to re-authenticate. Set well above a healthy silent check, so this never fires
    // on a working session.
    const initTimeout = new Promise<boolean>(resolve =>
      setTimeout(() => resolve(false), KeycloakAuthService.INIT_TIMEOUT_MS));

    const init = this.keycloak
      .init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
        // When the browser blocks 3rd-party cookies (Safari, incognito, Chrome's 3PCD, etc.) the
        // default `silentCheckSsoFallback: true` makes keycloak-js abandon the silent iframe and do a
        // FULL-PAGE redirect with `redirect_uri = location.href` — i.e. the page the user opened
        // (/XXX) instead of /callback. That URI is not a registered redirect and skips the
        // post-login chain. Disabling the fallback keeps check-sso iframe-only; seamless SSO is still
        // achieved by the top-level redirect that login() performs from /auth/login (where Keycloak
        // reads its first-party session cookie), and the guard's returnUrl=/XXX is preserved.
        silentCheckSsoFallback: false,
        // Defensive default: any keycloak-js redirect that doesn't pass an explicit redirectUri falls
        // back to this instead of location.href, so /callback is always the OIDC redirect target.
        redirectUri: `${window.location.origin}/callback`,
        checkLoginIframe: false,
        scope: this.scope,
        ...seededTokens
      })
      .catch(err => {
        console.error('Keycloak init failed', err);
        return false;
      });

    this.initPromise = Promise.race([init, initTimeout]);
    return this.initPromise;
  }

  /**
   * Trigger the Authorization Code + PKCE redirect to Keycloak. Returns a promise that does
   * not normally resolve in the current page lifetime — the browser navigates away.
   *
   * Renders the Keycloak login page in the app's currently-selected language on the FIRST paint via
   * `ui_locales` (keycloak-js's `locale` option), read from the shared `current_language` cookie. On the
   * initial, unauthenticated render no user attribute applies, so `ui_locales` is the effective selector
   * — and unlike `kc_locale` it's a passive OIDC hint that doesn't trigger a Keycloak redirect, so there
   * is no flash. The shared cookie stays the source of truth for the reverse direction and for
   * cold/direct visits to the login page.
   */
  public login(redirectUri?: string): Promise<void> {
    if (!this.keycloak) {
      return Promise.reject(new Error('Keycloak is not initialized'));
    }

    const options: Keycloak.KeycloakLoginOptions = {
      redirectUri: redirectUri || `${window.location.origin}/callback`,
      scope: this.scope
    };

    // Only forward a known-supported locale — an unexpected value (e.g. a tampered cookie) must never
    // reach the authorization request, both to preserve Keycloak's default and to avoid query injection.
    const rawLocale = this.sharedCookie.get('current_language');
    const uiLocale = rawLocale ? rawLocale.split(/[-_]/)[0].toLowerCase() : '';
    if (uiLocale && environment.supportedLanguages.includes(uiLocale)) {
      options.locale = uiLocale; // emitted by keycloak-js as `ui_locales`
    }

    return this.keycloak.login(options);
  }

  /**
   * RP-initiated logout against Keycloak. Ends the Keycloak SSO session and redirects back.
   */
  public logout(returnTo?: string): Promise<void> {
    const target = returnTo || window.location.origin;
    if (!this.keycloak) {
      window.location.href = target;
      return Promise.resolve();
    }
    return this.keycloak.logout({ redirectUri: target });
  }

  /**
   * Refresh the access token if it expires within `minValidity` seconds.
   * Returns true if the token was refreshed.
   */
  public updateToken(minValidity = 60): Promise<boolean> {
    if (!this.keycloak) return Promise.resolve(false);
    return this.keycloak.updateToken(minValidity);
  }

  /**
   * Decode the payload of a JWT. Kept for backward compatibility with callers that previously
   * relied on the old library's decodeToken helper.
   */
  public decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(decodeURIComponent(escape(atob(payload))));
    } catch {
      return null;
    }
  }

  /**
   * Fetches the OIDC userinfo profile from Keycloak via keycloak-js. Uses the instance's current
   * access token (the same token callers pass in), so the explicit argument is accepted for
   * call-site compatibility but not forwarded.
   */
  userInfo(_accessToken: string): Promise<any> {
    if (!this.keycloak) return Promise.reject(new Error('Keycloak is not initialized'));
    return this.keycloak.loadUserInfo();
  }
}
